import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Input } from "../../share/ui/input";
import { ArrowLeft } from "lucide-react";
import loginBg from "../../assets/img/login.png";
import maskgroup from "../../assets/img/maskgroup.png";
import logo from "../../assets/logo/evocare.svg";
import { verifyOtpCode } from "../../api/login/authService";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

export default function EnterCodePage() {
  const { t } = useTranslation();
  const [code, setCode] = useState<string[]>(Array(6).fill(""));
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState("");
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailFromQuery = params.get("email");
    if (emailFromQuery) {
      setUserEmail(decodeURIComponent(emailFromQuery));
    } else {
      console.warn("Không tìm thấy tham số 'email' trong URL.");
      navigate("/forgot-password", { replace: true });
    }
  }, [location.search, navigate]);

  const handleChange = (value: string, index: number) => {
    if (!/^\d?$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError("");

    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      if (nextInput) {
        (nextInput as HTMLInputElement).focus();
      }
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" || e.key === "Delete") {
      if (!code[index] && index > 0) {
        const prevInput = document.getElementById(`code-${index - 1}`);
        if (prevInput) {
          (prevInput as HTMLInputElement).focus();
        }
      }
    }
  };

  const handleNext = async () => {
    const fullCode = code.join("");
    if (fullCode.length === 6 && userEmail) {
      setIsLoading(true);
      try {
        setError("");
        const result = await verifyOtpCode(userEmail, fullCode);
        if (result) {
          localStorage.setItem("resetEmail", userEmail);
          localStorage.setItem("resetOtp", fullCode);
          toast.success(t("login.otpSuccess"));
          setTimeout(() => {
            navigate(`/reset-password?email=${encodeURIComponent(userEmail)}`);
          }, 1500);
        }
      } catch {
        setError(t("login.errorOtp"));
      } finally {
        setIsLoading(false);
      }
    } else {
      setError(t("login.errorFullCode"));
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left content */}
      <div className="w-full md:w-3/5 flex items-start pt-16 justify-center bg-white md:px-8 px-4 relative">
        <img
          src={maskgroup}
          alt="Mask Group"
          width={450}
          className="absolute top-0 left-10 pointer-events-none z-10"
          style={{ height: "auto" }}
        />

        <div className="max-w-md w-full relative z-20">
          <img
            src={logo}
            alt="EvoCare Logo"
            width={230}
            height={60}
            className="mb-6 ml-1"
            style={{ height: "auto" }}
          />
          <div className=" max-w-md w-full pr-2 pl-1 py-4  text-start">
            <h2 className="text-lg md:text-xl font-semibold mb-2">
              {t("login.verifyTitle")}
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              {t("login.verifyDesc", { email: userEmail || t("login.email") })}
            </p>

            <div className="mb-2 text-md text-gray-700">
              {t("login.enterCode")}
            </div>
            {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

            <div className="flex justify-between gap-2 mb-6">
              {code.map((digit, index) => (
                <Input
                  key={index}
                  id={`code-${index}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange(e.target.value, index)
                  }
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  className={`w-10 h-12 text-center text-lg rounded-md transition-colors font-bold  duration-200
        ${
          digit
            ? "text-orange-500 border-orange-500"
            : "text-gray-900 border-gray-300"
        }`}
                  aria-label={`Digit ${index + 1} of verification code`}
                />
              ))}
            </div>

            <button
              onClick={handleNext}
              disabled={isLoading}
              className="w-full py-3 bg-orange-600 text-white font-medium rounded-md hover:bg-orange-700 transition mb-4"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin h-5 w-5 mr-2 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {t("login.loading")}
                </span>
              ) : (
                t("login.next")
              )}
            </button>

            <button
              onClick={() => navigate(-1)}
              className="w-full py-3 bg-orange-100 text-orange-700 font-medium rounded-md hover:bg-orange-300 transition mb-4"
            >
              <div className="flex justify-center items-center">
                <ArrowLeft size={16} className="mr-2" /> {t("login.back")}
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Right background image */}
      <div className="hidden md:block md:w-4/5 relative">
        <img
          src={loginBg}
          alt="Login Background"
          className="object-cover brightness-95 w-full h-full"
        />
      </div>
    </div>
  );
}
